/**
 * QC Lifecycle Drawer (Phase 1 — E5)
 *
 * Right-side drawer showing the complete iteration history of an order.
 * Reads from GET /api/orders/{id}/qc-history.
 * Accessible to: submitter, QC reviewer, Team Leaders, Admin (gated by backend).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, ArrowUp, RotateCcw, XCircle, CheckCircle, Paperclip, Clock } from 'lucide-react';
import { formatDateTimeLocal } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DECISION_META = {
  approved: { icon: CheckCircle, color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Approved' },
  returned: { icon: RotateCcw,   color: 'text-amber-700',    bg: 'bg-amber-50',    border: 'border-amber-200',    label: 'Returned' },
  rejected: { icon: XCircle,     color: 'text-red-700',      bg: 'bg-red-50',      border: 'border-red-200',      label: 'Rejected' },
};

const formatDateTime = (s) => {
  if (!s) return '—';
  try { return formatDateTimeLocal(s); } catch { return s; }
};

const formatDuration = (startIso, endIso) => {
  if (!startIso || !endIso) return null;
  try {
    const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
    if (ms < 0) return null;
    const min = Math.floor(ms / 60000);
    if (min < 60) return `${min}m`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
  } catch { return null; }
};

const QCLifecycleDrawer = ({ orderId, orderNumber, isOpen, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchHistory = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/orders/${orderId}/qc-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
      else {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || 'Failed to load QC history');
      }
    } catch {
      setError('Network error loading QC history');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { if (isOpen) fetchHistory(); }, [isOpen, fetchHistory]);

  if (!isOpen) return null;

  const entries = (data?.history || []).slice().reverse(); // newest first
  const totalReturns = entries.filter(e => e.decision === 'returned').length;
  const totalRejects = entries.filter(e => e.decision === 'rejected').length;

  return (
    <div className="fixed inset-0 z-50" data-testid="qc-lifecycle-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute top-0 right-0 h-full w-full max-w-xl bg-white shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-base font-semibold text-gray-900">QC Lifecycle</h2>
            <p className="text-xs text-gray-500">{orderNumber || 'Order'}</p>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700 rounded-full hover:bg-gray-100" data-testid="qc-lifecycle-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          {loading && <div className="text-sm text-gray-500">Loading…</div>}
          {error && !loading && <div className="text-sm text-red-600">{error}</div>}
          {!loading && !error && entries.length === 0 && (
            <div className="text-sm text-gray-500">No QC history yet for this order.</div>
          )}

          {!loading && !error && entries.length > 0 && (
            <>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">Iterations</div>
                  <div className="text-lg font-semibold text-gray-900" data-testid="qc-lifecycle-iteration-count">{data?.qc_iteration || entries.length}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">Returns</div>
                  <div className="text-lg font-semibold text-amber-700">{totalReturns}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">Rejects</div>
                  <div className="text-lg font-semibold text-red-700">{totalRejects}</div>
                </div>
              </div>

              <div className="space-y-4">
                {entries.map((e, idx) => {
                  const meta = e.decision ? DECISION_META[e.decision] : null;
                  const Icon = meta?.icon;
                  const duration = formatDuration(e.submitted_at, e.decided_at);
                  return (
                    <div key={e.id || idx} className={`border rounded-lg overflow-hidden ${meta ? meta.border : 'border-gray-200'}`}>
                      <div className={`px-4 py-2 ${meta ? meta.bg : 'bg-blue-50'} border-b ${meta ? meta.border : 'border-gray-200'} flex items-center justify-between`}>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                            Iteration {e.iteration}
                          </span>
                          {!e.decision && (
                            <span className="inline-flex items-center gap-1 text-[10px] text-blue-700 bg-blue-100 rounded-full px-2 py-0.5 font-semibold">
                              <Clock className="w-3 h-3" /> IN REVIEW
                            </span>
                          )}
                          {meta && (
                            <span className={`inline-flex items-center gap-1 text-[10px] ${meta.color} bg-white rounded-full px-2 py-0.5 font-semibold uppercase tracking-wide border ${meta.border}`}>
                              {Icon && <Icon className="w-3 h-3" />} {meta.label}
                            </span>
                          )}
                        </div>
                        {duration && <span className="text-[11px] text-gray-500">{duration} in QC</span>}
                      </div>
                      <div className="px-4 py-3 space-y-2">
                        <div className="flex items-start gap-2 text-sm">
                          <ArrowUp className="w-3.5 h-3.5 text-gray-400 mt-0.5" />
                          <div className="flex-1">
                            <div className="text-gray-900">
                              <span className="font-medium">Submitted</span> by {e.submitted_by_name || '—'}
                            </div>
                            <div className="text-xs text-gray-500">{formatDateTime(e.submitted_at)}</div>
                            {e.submitted_notes && (
                              <div className="mt-1 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded px-2 py-1 italic" style={{ whiteSpace: 'pre-wrap' }}>
                                &ldquo;{e.submitted_notes}&rdquo;
                              </div>
                            )}
                          </div>
                        </div>
                        {meta && (
                          <div className="flex items-start gap-2 text-sm">
                            {Icon && <Icon className={`w-3.5 h-3.5 mt-0.5 ${meta.color}`} />}
                            <div className="flex-1">
                              <div className="text-gray-900">
                                <span className="font-medium">{meta.label}</span> by {e.decided_by_name || '—'}
                              </div>
                              <div className="text-xs text-gray-500">{formatDateTime(e.decided_at)}</div>
                              {(e.decision_category || e.decision_error_type) && (
                                <div className="text-xs text-gray-600 mt-1">
                                  {e.decision_category && <span className="font-medium">{e.decision_category.replace(/_/g,' ')}</span>}
                                  {e.decision_error_type && <span> · {e.decision_error_type}</span>}
                                </div>
                              )}
                              {e.decision_reason && (
                                <div className="mt-1 text-xs text-gray-800 bg-gray-50 border border-gray-200 rounded px-2 py-1" style={{ whiteSpace: 'pre-wrap' }}>
                                  {e.decision_reason}
                                </div>
                              )}
                              {Array.isArray(e.evidence_file_ids) && e.evidence_file_ids.length > 0 && (
                                <div className="mt-1.5 flex flex-wrap gap-1.5">
                                  {e.evidence_file_ids.map(fid => (
                                    <a key={fid} href={`${API}/files/${fid}/download`} target="_blank" rel="noreferrer"
                                      className="inline-flex items-center gap-1 text-[11px] text-blue-700 hover:text-blue-900 bg-white border border-gray-200 rounded-full px-2 py-0.5"
                                      data-testid={`lifecycle-evidence-${fid}`}>
                                      <Paperclip className="w-3 h-3" /> {fid.slice(0, 8)}…
                                    </a>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QCLifecycleDrawer;
